/**
 * Portal Application Layout
 *
 * Provides the portal layout structure with sidebar navigation,
 * header, and main content area. Same pattern as admin AppLayout.
 *
 * Super admins access the portal via an org switcher — they skip
 * onboarding and must select an organization before proceeding.
 */

import { useState } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/auth/useAuth';
import { usePortalOnboardingStatus } from '@/hooks/api/usePortalOnboarding';
import { useOrgContext } from '@/contexts/OrgContext';
import { usePortalHome, usePortalPathAllowed } from '@/hooks/portal/usePortalHome';
import { PortalSidebar } from './PortalSidebar';
import { PortalHeader } from './PortalHeader';
import { OrgSwitcher } from './OrgSwitcher';
import { BillingBanner } from './BillingBanner';

export function PortalLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { isSuperAdmin } = useAuth();
  const { orgId } = useOrgContext();
  const isOnboardingPage = location.pathname.startsWith('/portal/onboarding');

  // All hooks must be called before any early returns (Rules of Hooks).
  // Super admins skip onboarding, so the query is disabled for them.
  const { data: onboardingStatus, isLoading: onboardingLoading } = usePortalOnboardingStatus();
  const home = usePortalHome();
  const pathAllowed = usePortalPathAllowed(location.pathname);

  // Super admins need to select an org before accessing portal
  if (isSuperAdmin && !orgId) {
    return (
      <div className="min-h-screen bg-background lg:flex">
        <div className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 ease-in-out lg:relative lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}>
          <PortalSidebar onClose={() => setSidebarOpen(false)} />
        </div>
        <div className="flex-1 min-w-0 overflow-hidden lg:flex lg:flex-col">
          <PortalHeader onMenuClick={() => setSidebarOpen(true)} />
          <main className="flex-1 p-6">
            <OrgSwitcher />
          </main>
        </div>
      </div>
    );
  }

  if (!isSuperAdmin && !isOnboardingPage) {
    if (onboardingLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (onboardingStatus && onboardingStatus.step !== 'done') {
      return <Navigate to="/portal/onboarding" replace />;
    }
  }

  // A restricted member's nav is a description of their whole surface, not a
  // menu — pages it omits are unreachable, not merely unlisted. Without this
  // the sidebar restriction was cosmetic: URLs, browser history and any
  // history-derived breadcrumb still walked straight past it (CUR-1395).
  // Denials land on the member's own home, so this can bounce but never strand.
  if (!pathAllowed) {
    return <Navigate to={home} replace />;
  }

  return (
    <div className="min-h-screen bg-background lg:flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-200 ease-in-out lg:relative lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <PortalSidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Main content area */}
      <div className="flex-1 min-w-0 overflow-hidden lg:flex lg:flex-col">
        <PortalHeader onMenuClick={() => setSidebarOpen(true)} />

        <main className="flex-1 p-6">
          <BillingBanner />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
